import React, { useState, useEffect, useCallback } from 'react';
import { chatConversationService, ConversationHistory, ChatMessage } from '../lib/chatConversationService';
import { useAuth } from '../contexts/AuthContext';

interface ChatHistoryProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ChatHistory: React.FC<ChatHistoryProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<ConversationHistory[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<{
    totalConversations: number;
    totalMessages: number;
    avgMessagesPerConversation: number;
    avgResponseTime: number;
  } | null>(null);

  const loadConversations = useCallback(async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      const data = await chatConversationService.getUserConversations(user.id, 20);
      setConversations(data);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const loadStats = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const data = await chatConversationService.getConversationStats(user.id);
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  }, [user?.id]);

  useEffect(() => {
    if (isOpen && user?.id) {
      loadConversations();
      loadStats();
    }
  }, [isOpen, user?.id, loadConversations, loadStats]);

  const loadConversationMessages = async (conversationId: string) => {
    setLoading(true);
    try {
      const data = await chatConversationService.getConversationMessages(conversationId);
      setMessages(data);
      setSelectedConversation(conversationId);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (startedAt: string, endedAt?: string) => {
    const start = new Date(startedAt);
    const end = endedAt ? new Date(endedAt) : new Date();
    const diff = end.getTime() - start.getTime();
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h3 className="font-semibold text-gray-900">Chat History</h3>
            <p className="text-xs text-gray-500">Your conversation history with AI support</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Conversations List */}
          <div className="w-1/3 border-r border-gray-200 flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <h4 className="font-medium text-gray-900 mb-2">Conversations</h4>
              {stats && (
                <div className="text-xs text-gray-500 space-y-1">
                  <div>Total: {stats.totalConversations}</div>
                  <div>Messages: {stats.totalMessages}</div>
                  <div>Avg per chat: {stats.avgMessagesPerConversation}</div>
                  <div>Avg response: {stats.avgResponseTime}ms</div>
                </div>
              )}
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center text-gray-500">Loading...</div>
              ) : conversations.length === 0 ? (
                <div className="p-4 text-center text-gray-500">No conversations yet</div>
              ) : (
                <div className="space-y-1">
                  {conversations.map((conv) => (
                    <button
                      key={conv.conversation_id}
                      onClick={() => loadConversationMessages(conv.conversation_id)}
                      className={`w-full text-left p-3 hover:bg-gray-50 transition-colors ${
                        selectedConversation === conv.conversation_id ? 'bg-blue-50 border-r-2 border-blue-500' : ''
                      }`}
                    >
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {conv.first_message || 'No message'}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {formatDate(conv.started_at)} • {conv.total_messages} messages
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        Duration: {formatDuration(conv.started_at, conv.ended_at)}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Messages View */}
          <div className="flex-1 flex flex-col">
            {selectedConversation ? (
              <>
                <div className="p-4 border-b border-gray-200">
                  <h4 className="font-medium text-gray-900">Messages</h4>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {loading ? (
                    <div className="text-center text-gray-500">Loading messages...</div>
                  ) : messages.length === 0 ? (
                    <div className="text-center text-gray-500">No messages found</div>
                  ) : (
                    messages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-lg px-4 py-2 ${
                            message.role === 'user'
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-900'
                          }`}
                        >
                          <div className="whitespace-pre-wrap text-sm">{message.content}</div>
                          <div className={`text-xs mt-1 ${
                            message.role === 'user' ? 'text-blue-100' : 'text-gray-500'
                          }`}>
                            {formatDate(message.created_at)}
                            {message.is_fallback && ' • Fallback'}
                            {message.model_used && ` • ${message.model_used}`}
                            {message.response_time_ms && ` • ${message.response_time_ms}ms`}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-500">
                Select a conversation to view messages
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}; 